import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, useTopics } from '../../api';
import type { TalkingPointDto, TopicDto } from '../../api/generated/client';
import type { AdvisoryNeedDto } from '../../interfaces/advisory-need';
import type { IThinTopic } from '../../interfaces/topic';
import { buildAdvisoryNeedTask } from '../../utils/eventUtils';
import { hasEntry } from '../../utils/utils';
import { NeedEventModal } from '../topic/modals/NeedEventModal';
import { SelectNeedModal } from './modals/SelectNeedModal';
import { SelectTalkingPointModal } from './modals/SelectTalkingPointModal';
import { SelectTopicModal } from './modals/SelectTopicModal';

interface IProps {
    onClose: () => void;
}

export const AddNeedFlowModal: FunctionComponent<IProps> = ({ onClose }) => {
    const { i18n } = useTranslation();
    const { data: activeClient } = useClient();
    const { data: topics } = useTopics();

    const [isSelectTopicModalOpen, setIsSelectTopicModalOpen] = useState(true);
    const [isSelectNeedModalOpen, setIsSelectNeedModalOpen] = useState(false);
    const [isZafireTakingPointModalOpen, setIsZafireTakingPointModalOpen] = useState(false);
    const [isZafireNeedEventModalOpen, setIsZafireNeedEventModalOpen] = useState(false);
    const [activeTopic, setActiveTopic] = useState<IThinTopic | undefined>(undefined);
    const [activeTalkingPoint, setActiveTalkingPoint] = useState<TalkingPointDto | undefined>(undefined);
    const [activeNeed, setActiveNeed] = useState<AdvisoryNeedDto>();

    if (!hasEntry(topics)) {
        return null;
    }

    const onBackToSelectTalkingPoint = () => {
        setActiveNeed(undefined);
        setActiveTalkingPoint(undefined);
        setIsSelectNeedModalOpen(false);
        setIsZafireTakingPointModalOpen(true);
    };
    const onBackToSelectNeed = () => {
        setActiveNeed(undefined);
        setIsZafireNeedEventModalOpen(false);
        setIsSelectNeedModalOpen(true);
    };

    const onTopicSelected = (topic?: IThinTopic) => {
        setActiveTopic(topic);
        setActiveTalkingPoint(undefined);
        setActiveNeed(undefined);
        setIsSelectTopicModalOpen(false);
        setIsZafireTakingPointModalOpen(true);
        setIsSelectNeedModalOpen(false);
    };
    const onTalkingPointSelected = (talkingPoint: TalkingPointDto) => {
        setActiveTalkingPoint(talkingPoint);
        setIsZafireTakingPointModalOpen(false);
        setIsSelectNeedModalOpen(true);
    };
    const onNeedSelected = (selectedNeed?: AdvisoryNeedDto) => {
        setActiveNeed(selectedNeed);
        setIsSelectNeedModalOpen(false);
        setIsZafireNeedEventModalOpen(true);
    };

    return (
        <>
            {isSelectTopicModalOpen && (
                <SelectTopicModal isOpen={isSelectTopicModalOpen} isAddEvent={false} onClose={onClose} onTopicSelected={onTopicSelected} />
            )}
            {isZafireTakingPointModalOpen && (
                <SelectTalkingPointModal
                    isOpen={isZafireTakingPointModalOpen}
                    topics={topics}
                    selectedTopic={activeTopic}
                    onClose={onClose}
                    onTopicSelected={onTopicSelected}
                    onTalkingPointSelected={onTalkingPointSelected}
                />
            )}
            {isSelectNeedModalOpen && (
                <SelectNeedModal
                    isOpen={isSelectNeedModalOpen}
                    topics={topics}
                    selectedTopic={activeTopic}
                    selectedTalkingPoint={activeTalkingPoint}
                    onClose={onClose}
                    onBack={onBackToSelectTalkingPoint}
                    onTopicSelected={onTopicSelected}
                    onNeedSelected={onNeedSelected}
                />
            )}
            {isZafireNeedEventModalOpen && (
                <NeedEventModal
                    onClose={onClose}
                    event={buildAdvisoryNeedTask(i18n.language, activeTopic as TopicDto, activeTalkingPoint, activeNeed, activeClient)}
                    onBack={onBackToSelectNeed}
                    isOpen={isZafireNeedEventModalOpen}
                />
            )}
        </>
    );
};
